import React, { useEffect, useReducer } from 'react';
import { AvatarIcon, GridItemLayout, GridLayout, Pager, Searchfield, colourString, useMatchScreenWidth } from '@keplerco/core';
import { EntitiesCardWidget } from '../../../components/cards/entities-card.widget';
import { EmptyState } from '../../../components/general/empty-state/empty-state';
import { SortField, OrganizationLevelType, FetchType, FetchStatus } from '../../../enums';
import { extractHighestOrganizationLevel } from '../../../library/helpers/permissions/extract-highest-organization-level';
import { CompanyRoleListItemResponse } from '../../../models/overmind/company-entity';
import { CompanyRoleAssigneesSearchParams } from '../../../models/overmind/search-params';
import { PagePath } from '../../../navigation/navigation.enums';
import { useAppActions, useAppState } from '../../../overmind';
import * as store from './your-career-people.store';
import styles from './your-career-people.module.css';

export default function YourCareerPeopleWidget({ role }: { role: CompanyRoleListItemResponse }) {
  const actions = useAppActions();
  const { companyVariables, fetchState, permissions } = useAppState();

  const [state, dispatch] = useReducer(store.reducer, store.initialState);

  const isMobile = useMatchScreenWidth('mobile');

  useEffect(() => {
    const organizationLevel = extractHighestOrganizationLevel(permissions?.roleSkillManagement?.organizationLevels);
    const request: CompanyRoleAssigneesSearchParams = {
      search: undefined,
      sortAscending: true,
      sortField: SortField.Name,
      pageSize: 9,
      page: 1,
      organizationLevel: organizationLevel?.organizationLevel ?? OrganizationLevelType.Learner,
      companySlug: companyVariables.slug,
      departmentSlug: undefined,
      teamSlug: undefined,
      learnerSlug: undefined,
      roleSlug: role.slug!,
    };
    dispatch({ type: store.YourCareerPeopleActionTypes.SetRequest, payload: request });
  }, [role, companyVariables.slug]);

  async function updateData() {
    if (!state.request) return actions.stopLoader(PagePath.yourCareerRole);
    const response = PagePath.yourCareerRole !== PagePath.yourCareerRole ? await actions.getCompanyRoleAssignees(state.request) : await actions.getUserCompanyRoleAssignees(state.request);
    dispatch({ type: store.YourCareerPeopleActionTypes.SetData, payload: response });
    actions.stopLoader(PagePath.yourCareerRole);
  }

  useEffect(() => {
    async function getData() {
      if (!state.request) return;
      actions.startLoader({ path: PagePath.yourCareerRole, type: FetchType.PageFetching });
      const response = PagePath.yourCareerRole !== PagePath.yourCareerRole ? await actions.getCompanyRoleAssignees(state.request) : await actions.getUserCompanyRoleAssignees(state.request);
      if (!!response?.assignees.length) dispatch({ type: store.YourCareerPeopleActionTypes.SetData, payload: response });
      actions.stopLoader(PagePath.yourCareerRole);
    }

    !state.data ? getData() : updateData();
  }, [state.request]);

  return (
    <EntitiesCardWidget title="People" description="People assigned to the role">
      {!!state.data && (
        <Searchfield
          loading={fetchState[PagePath.yourCareerRole].status === FetchStatus.Active && fetchState[PagePath.yourCareerRole].type === FetchType.Custom}
          label="Search people"
          onInput={search => {
            if (!state.request) return;
            actions.startLoader({ path: PagePath.yourCareerRole, type: FetchType.Custom });
            dispatch({ type: store.YourCareerPeopleActionTypes.SetRequest, payload: { ...state.request, search, page: 1 } });
          }}
          responsive
        />
      )}

      <div style={{ minHeight: 270, marginTop: 15, marginBottom: !!state.data && state.data.totalPages > 1 ? 15 : 0 }}>
        {!!state.data?.assignees.length ? (
          <GridLayout columnCount={isMobile ? 1 : 3}>
            {state.data.assignees.map(person => (
              <GridItemLayout key={person.slug}>
                <div className={styles.personCard}>
                  <div className={styles.personCardIcon}>
                    <AvatarIcon name={{ firstName: person.name!.split(' ')[0], lastName: person.name!.split(' ')[1] }} />
                  </div>

                  <div className={styles.personCardContent}>
                    <h5>{person.name}</h5>
                    <small style={{ color: colourString('lilac') }}>{person.department}</small>
                  </div>
                </div>
              </GridItemLayout>
            ))}
          </GridLayout>
        ) : (
          <EmptyState title="No people found" />
        )}
      </div>

      {!!state.data && state.data?.totalPages > 1 && (
        <Pager
          activePageNumber={state.request?.page ?? 1}
          pageCount={state.data?.totalPages}
          onPageChange={page => {
            if (!state.request || page === state.request.page) return;
            actions.startLoader({ path: PagePath.yourCareerRole, type: FetchType.Custom });
            dispatch({ type: store.YourCareerPeopleActionTypes.SetRequest, payload: { ...state.request, page } });
          }}
        />
      )}
    </EntitiesCardWidget>
  );
}
